import { toQueryString } from '@hc-frontend/core-utils-url';

import { identifyHttpResult } from './identify-http-result';
import type { IHttpCall, IHttpRequest, IHttpResponse } from './types';

const getDefaultHeaders = () => {
  const defaultHeaders = new Headers();
  defaultHeaders.set('Content-Type', 'application/json');
  return defaultHeaders;
};

export const fetchBasedCall: IHttpCall = <TRequest, TResponse>({
  url,
  method,
  body,
  headers,
  cache,
  queryParams,
  schemas,
  next,
  signal,
}: IHttpRequest<TRequest>): Promise<IHttpResponse<TResponse>> => {
  const actualHeaders = getDefaultHeaders();
  if (headers)
    Object.entries(
      schemas?.headers ? schemas.headers.parse(headers) : headers,
    ).forEach(([key, value]) => actualHeaders.set(key, value as string));

  let actualBody: FormData | string | undefined;
  if (typeof body === 'string') {
    actualBody = body;
  } else if (typeof body === 'object') {
    if (body instanceof FormData) {
      actualHeaders.delete('content-type');
      actualBody = body;
    } else if (actualHeaders.get('content-type') === 'application/json') {
      actualBody = JSON.stringify(
        schemas?.request
          ? schemas.request.describe('Request body schema').parse(body)
          : body,
      );
    }
  }

  let query = '';
  if (queryParams) {
    query = `?${toQueryString(schemas?.query ? schemas.query.describe('Query parameters schema').parse(queryParams) : queryParams)}`;
  }

  return fetch(`${url}${query}`, {
    method,
    headers: actualHeaders,
    body: actualBody,
    cache,
    next,
    signal,
  } as RequestInit).then((rawResponse) => {
    const contentType = rawResponse.headers.get('content-type');
    const result = identifyHttpResult(rawResponse.status);

    const parseBodyToJson =
      rawResponse.body != null &&
      contentType != null &&
      contentType.toLowerCase().includes('application/json');

    const response = parseBodyToJson
      ? rawResponse.json().then((jsonBody) => {
          if (rawResponse.ok && schemas?.response) {
            const parsedBody = schemas.response
              .describe('Response body schema')
              .safeParse(jsonBody);

            if (parsedBody.success) {
              return {
                status: rawResponse.status,
                result,
                body: parsedBody.data as TResponse,
              };
            }

            // Log any error if the schema parse was not successful

            console.trace(parsedBody.error.issues);
          }

          return {
            status: rawResponse.status,
            result,
            body: jsonBody as TResponse,
          };
        })
      : Promise.resolve({ status: rawResponse.status, result });

    return response;
  });
};
